import { useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "@/app/appStore";
import {
  GondolaChange,
  GondolaChangeGroup,
  GondolaChangeGroupsData,
  SKUSectionChange,
  SKUSectionChangeGroup,
} from "@/entities/gondola-change-groups/model/types";
import {
  setDataSource,
  setFileData,
  clearDataSource,
  selectFileData,
  selectSelectedGondolaChangeGroupId,
  selectSelectedGondolaChangeId,
  selectSelectedSkuSectionChangeId,
  selectSelectedSkuSectionChangeGroupTableData,
  selectTrackAvatarBindings,
  selectJourneyTrackRelations,
  selectTrackIdsInOcclusion,
  selectRecoveredTrackIdMapping,
  selectVisualizeTrackProjections,
  setTrackAvatarBindings,
  setJourneyTrackRelations,
  setTrackIdsInOcclusion,
  setRecoveredTrackIdMapping,
  clearTrackData,
  TrackAvatarBinding,
  JourneyTrackRelation,
  selectSelectedDetectionId,
} from "@/entities/gondola-change-groups/model/slice";
import {
  selectFilteredChangeGroups,
  selectGondolaChangeGroupsFilters,
  selectFilteredSkuSectionChangeGroups,
  selectFilteredGondolaChanges,
  selectSelectedGondolaChange,
} from "@/entities/gondola-change-groups/model/selectors";
import {
  addCompositeIdsToChangeGroups,
  processJourneyTrackRelations,
  extractTrackIdsInOcclusion,
  createRecoveredTrackIdMapping,
  createComprehensiveTrackAvatarBindings,
  findTrackAvatarBinding,
} from "../lib/helpers";
import {
  selectCurrentCamera,
  selectSelectedPolygon,
  selectSelectedSkuSectionId,
} from "@/entities/annotation";

export interface UseGondolaChangeGroupsDataResult {
  data: GondolaChangeGroupsData | null;
  filteredData: GondolaChangeGroup[];
  selectedGondolaChangeGroup: GondolaChangeGroup | null;
  filteredSkuSectionChangeGroups: SKUSectionChangeGroup[];
  selectedSkuSectionGroup: SKUSectionChangeGroup | null;
  filteredGondolaChanges: GondolaChange[];
  selectedGondolaChange: GondolaChange | null;
  selectedSkuSectionChanges: SKUSectionChange[];
  selectedSkuSectionChange: SKUSectionChange | null;
  selectedSkuSectionChangeWithDetection: SKUSectionChange | null;
  skuSectionChangesForCurrentCamera: SKUSectionChange[];
  skuSectionChangesForCurrentCameraGroupTableData: SKUSectionChange[] | null;
  skuSectionChangeGroupsForSelectedGondolaChange: SKUSectionChangeGroup[];
  loadFromFile: (file: File) => Promise<void>;
  loadFromEndpoint: (rawData: GondolaChangeGroupsData) => void;
  clearData: () => void;
  orderId: string | null;
  handleSkuSectionProxsHighlighting: (
    skuSectionId: number | null,
    highlightCallback: (
      proxIds: string[],
      positiveScoreIndices: number[]
    ) => void,
    clearCallback: () => void
  ) => void;
  highlightSingleProx: (
    trackId: number,
    timeStart: string,
    handId: number,
    highlightCallback: (
      proxIds: string[],
      positiveScoreIndices: number[]
    ) => void,
    clearCallback: () => void
  ) => void;
  findFirstSkuSectionChange: (skuSectionId: number) => SKUSectionChange | null;
  findFirstSkuSectionChangeWithDetection: (
    skuSectionId: number
  ) => SKUSectionChange | null;
  findSkuSectionChangeWithTriangulationId: (
    skuSectionId: number,
    gondolaChangeCameraCompositeId: string,
    triangulationId: string
  ) => SKUSectionChange | null;
  getFirstFilteredGondolaChange: (
    gondolaChangeGroupId: string | null
  ) => GondolaChange | null;
  trackAvatarBindings: Record<number, TrackAvatarBinding>;
  journeyTrackRelations: JourneyTrackRelation[];
  trackIdsInOcclusion: number[];
  recoveredTrackIdMapping: Record<number, number>;
  findTrackAvatar: (trackId: number) => TrackAvatarBinding | null;
}

export const useGondolaChangeGroupsData =
  (): UseGondolaChangeGroupsDataResult => {
    const dispatch = useAppDispatch();

    const fileDataFromRedux = useAppSelector(selectFileData);
    const trackAvatarBindings = useAppSelector(selectTrackAvatarBindings);
    const journeyTrackRelations = useAppSelector(selectJourneyTrackRelations);
    const trackIdsInOcclusion = useAppSelector(selectTrackIdsInOcclusion);
    const recoveredTrackIdMapping = useAppSelector(
      selectRecoveredTrackIdMapping
    );
    const visualizeTrackProjections = useAppSelector(
      selectVisualizeTrackProjections
    );

    const data = fileDataFromRedux;

    const filters = useAppSelector(selectGondolaChangeGroupsFilters);
    const currentCamera = useAppSelector(selectCurrentCamera);
    const selectedSkuSectionId = useAppSelector(selectSelectedSkuSectionId);
    const selectedDetectionId = useAppSelector(selectSelectedDetectionId);
    const selectedPolygon = useAppSelector(selectSelectedPolygon);
    const selectedSkuSectionChangeGroupTableData = useAppSelector(
      selectSelectedSkuSectionChangeGroupTableData
    );

    const processGondolaChangeGroupsData = useCallback(
      (rawData: GondolaChangeGroupsData, dataSourceType: "file" | "api") => {
        const dataWithIds = addCompositeIdsToChangeGroups(rawData);

        const journeyTrackRelations = processJourneyTrackRelations(
          rawData.track_ids_per_journey_id || {},
          rawData.recovered_track_ids_per_track_id || {}
        );

        const trackIdsInOcclusion = extractTrackIdsInOcclusion(
          dataWithIds.gondola_change_groups,
          journeyTrackRelations
        );

        const recoveredTrackIdMapping = createRecoveredTrackIdMapping(
          rawData.recovered_track_ids_per_track_id || {}
        );

        const trackAvatarBindings = createComprehensiveTrackAvatarBindings(
          journeyTrackRelations,
          trackIdsInOcclusion
        );

        dispatch(setFileData(dataWithIds));
        dispatch(setJourneyTrackRelations(journeyTrackRelations));
        dispatch(setTrackIdsInOcclusion(trackIdsInOcclusion));
        dispatch(setRecoveredTrackIdMapping(recoveredTrackIdMapping));
        dispatch(setTrackAvatarBindings(trackAvatarBindings));

        dispatch(
          setDataSource({
            type: dataSourceType,
            fileData: dataWithIds,
          })
        );
      },
      [dispatch]
    );

    const loadFromFile = useCallback(
      async (file: File) => {
        try {
          const text = await file.text();
          const rawData: GondolaChangeGroupsData = JSON.parse(text);
          processGondolaChangeGroupsData(rawData, "file");
        } catch (error) {
          console.error("Failed to load file:", error);
        }
      },
      [processGondolaChangeGroupsData]
    );

    const loadFromEndpoint = useCallback(
      (rawData: GondolaChangeGroupsData) => {
        try {
          processGondolaChangeGroupsData(rawData, "api");
        } catch (error) {
          console.error("Failed to load endpoint data:", error);
        }
      },
      [processGondolaChangeGroupsData]
    );

    const filteredData = useMemo(() => {
      if (!data) return [];
      return selectFilteredChangeGroups.resultFunc(
        data.gondola_change_groups || [],
        filters
      );
    }, [data, filters]);

    const selectedGondolaChangeGroupId = useAppSelector(
      selectSelectedGondolaChangeGroupId
    );

    const selectedGondolaChangeId = useAppSelector(
      selectSelectedGondolaChangeId
    );

    const selectedSkuSectionChangeId = useAppSelector(
      selectSelectedSkuSectionChangeId
    );

    const selectedGondolaChangeGroup = useMemo(() => {
      if (!data || !selectedGondolaChangeGroupId) return null;
      return (
        data.gondola_change_groups.find(
          (group) =>
            group.gondola_change_group_composite_id ===
            selectedGondolaChangeGroupId
        ) || null
      );
    }, [data, selectedGondolaChangeGroupId]);

    const filteredSkuSectionChangeGroups = useMemo(() => {
      return selectFilteredSkuSectionChangeGroups.resultFunc(
        selectedGondolaChangeGroup,
        filters
      );
    }, [selectedGondolaChangeGroup, filters]);

    const filteredGondolaChanges = useMemo(() => {
      return selectFilteredGondolaChanges.resultFunc(
        selectedGondolaChangeGroup,
        filteredSkuSectionChangeGroups
      );
    }, [selectedGondolaChangeGroup, filteredSkuSectionChangeGroups]);

    const selectedGondolaChange = useMemo(() => {
      return selectSelectedGondolaChange.resultFunc(
        {
          selectedGondolaChangeGroupId,
          selectedGondolaChangeId,
          selectedSkuSectionChangeId,
          selectedDetectionId,
          selectedTriangulationId: null,
          filters,
          dataSource: null,
          fileData: null,
          selectedSkuSectionChangeGroupTableData: null,
          trackAvatarBindings: {},
          journeyTrackRelations: [],
          trackIdsInOcclusion: [],
          recoveredTrackIdMapping: {},
          visualizeTrackProjections,
          closestGondolaDistItems: [],
          selectedClosestGondolaDistItem: null,
        },
        filteredGondolaChanges
      );
    }, [
      selectedGondolaChangeGroupId,
      selectedGondolaChangeId,
      selectedSkuSectionChangeId,
      selectedDetectionId,
      filteredGondolaChanges,
      filters,
      visualizeTrackProjections,
    ]);

    const skuSectionChangeGroupsForSelectedGondolaChange = useMemo(() => {
      if (
        !filteredSkuSectionChangeGroups.length ||
        !selectedGondolaChange ||
        !selectedGondolaChangeGroup
      )
        return [];

      const skuSectionChangeGroups = filteredSkuSectionChangeGroups.filter(
        (sscg) =>
          sscg.sku_section_changes.some(
            (change) =>
              change.gondola_change_camera_composite_id ===
              selectedGondolaChange?.gondola_change_camera_composite_id
          )
      );

      return skuSectionChangeGroups;
    }, [
      filteredSkuSectionChangeGroups,
      selectedGondolaChangeGroup,
      selectedGondolaChange,
    ]);

    const skuSectionChangesForCurrentCamera = useMemo(() => {
      if (!currentCamera || !selectedGondolaChangeGroup) return [];

      const allSkuSectionChanges =
        selectedGondolaChangeGroup?.sku_section_change_groups?.flatMap(
          (sscg) => sscg.sku_section_changes
        ) || [];

      return allSkuSectionChanges.filter(
        (change) =>
          change.gondola_change_camera_composite_id ===
          selectedGondolaChange?.gondola_change_camera_composite_id
      );
    }, [currentCamera, selectedGondolaChangeGroup, selectedGondolaChange]);

    const skuSectionChangesForCurrentCameraGroupTableData = useMemo(() => {
      if (selectedSkuSectionChangeGroupTableData === null) {
        return null;
      }

      if (!selectedSkuSectionChangeGroupTableData || !selectedGondolaChange) {
        return [];
      }

      const skuSectionChanges = selectedSkuSectionChangeGroupTableData?.flatMap(
        (sscg) => sscg.sku_section_changes
      );

      return skuSectionChanges?.filter(
        (change) =>
          change.gondola_change_camera_composite_id ===
          selectedGondolaChange?.gondola_change_camera_composite_id
      );
    }, [selectedSkuSectionChangeGroupTableData, selectedGondolaChange]);

    const selectedSkuSectionGroup = useMemo(() => {
      if (
        !selectedGondolaChangeGroup?.sku_section_change_groups ||
        !selectedSkuSectionId
      ) {
        return null;
      }

      /**
       * There might be multiple sku section change groups for the same sku section id.
       * We need to find the one that has the same gondola_change_camera_composite_id.
       */

      const skuSectionChangeGroups =
        selectedGondolaChangeGroup?.sku_section_change_groups.filter(
          (group) => group.sku_section_id === selectedSkuSectionId
        );

      return (
        skuSectionChangeGroups?.find((group) => {
          return group.sku_section_changes.some(
            (change) =>
              change.gondola_change_camera_composite_id ===
              selectedGondolaChange?.gondola_change_camera_composite_id
          );
        }) || null
      );
    }, [
      selectedGondolaChangeGroup,
      selectedSkuSectionId,
      selectedGondolaChange?.gondola_change_camera_composite_id,
    ]);

    const selectedSkuSectionChanges = useMemo(() => {
      if (!selectedSkuSectionGroup?.sku_section_changes?.length) {
        return [];
      }

      return selectedSkuSectionGroup.sku_section_changes.filter(
        (change) =>
          change.gondola_change_camera_composite_id ===
            selectedGondolaChange?.gondola_change_camera_composite_id &&
          change.sku_section_id === selectedPolygon?.sku_section_id
      );
    }, [selectedSkuSectionGroup, selectedGondolaChange, selectedPolygon]);

    const selectedSkuSectionChange = useMemo(() => {
      if (!selectedSkuSectionChanges.length) {
        return null;
      }
      return (
        selectedSkuSectionChanges?.find(
          (change) =>
            change.gondola_change_camera_composite_id ===
            selectedSkuSectionChangeId
        ) || null
      );
    }, [selectedSkuSectionChanges, selectedSkuSectionChangeId]);

    const selectedSkuSectionChangeWithDetection = useMemo(() => {
      if (!selectedSkuSectionChanges.length) {
        return null;
      }
      return (
        selectedSkuSectionChanges?.find((change) => {
          const detectionId = String(change.detection?.id);
          const triangulationId = change.triangulation?.triangulation_id;
          const isSelected =
            selectedSkuSectionChangeId ===
              change.gondola_change_camera_composite_id &&
            (detectionId === selectedDetectionId ||
              triangulationId === selectedDetectionId);

          return isSelected;
        }) || null
      );
    }, [
      selectedSkuSectionChanges,
      selectedSkuSectionChangeId,
      selectedDetectionId,
    ]);

    const clearData = useCallback(() => {
      dispatch(clearDataSource());
      dispatch(clearTrackData());
    }, [dispatch]);

    // Function to find track avatar binding for any track ID (original or recovered)
    const findTrackAvatar = useCallback(
      (trackId: number): TrackAvatarBinding | null => {
        return findTrackAvatarBinding(
          trackId,
          trackAvatarBindings,
          recoveredTrackIdMapping
        );
      },
      [trackAvatarBindings, recoveredTrackIdMapping]
    );

    const handleSkuSectionProxsHighlighting = useCallback(
      (
        skuSectionId: number | null,
        highlightCallback: (
          proxIds: string[],
          positiveScoreIndices: number[]
        ) => void,
        clearCallback: () => void
      ) => {
        clearCallback();

        if (!skuSectionId || !selectedGondolaChangeGroup) {
          return;
        }

        const correspondingSkuSectionGroup =
          filteredSkuSectionChangeGroups.find(
            (sscg) => sscg.sku_section_id === skuSectionId
          );

        if (!correspondingSkuSectionGroup) {
          return;
        }

        const proxsMatchScores =
          correspondingSkuSectionGroup.proxs_match_scores;
        const positiveScoreIndices = proxsMatchScores
          .map((score, index) => ({ score, index }))
          .filter(({ score }) => score > 0)
          .map(({ index }) => index);

        if (positiveScoreIndices.length === 0) {
          return;
        }

        const intersectedProxs = selectedGondolaChangeGroup.intersected_proxs;
        const proxIds = intersectedProxs.map(
          (prox, index) =>
            `prox-${prox.track_id}-${prox.hand_id}-${prox.time_start}-${index}`
        );

        highlightCallback(proxIds, positiveScoreIndices);
      },
      [selectedGondolaChangeGroup, filteredSkuSectionChangeGroups]
    );

    const highlightSingleProx = useCallback(
      (
        trackId: number,
        timeStart: string,
        handId: number,
        highlightCallback: (
          proxIds: string[],
          positiveScoreIndices: number[]
        ) => void,
        clearCallback: () => void
      ) => {
        clearCallback();

        if (!selectedGondolaChangeGroup) {
          return;
        }

        const intersectedProxs = selectedGondolaChangeGroup.intersected_proxs;

        const matchingProxIndex = intersectedProxs.findIndex(
          (prox) =>
            prox.track_id === trackId &&
            prox.time_start === timeStart &&
            prox.hand_id === handId
        );

        if (matchingProxIndex === -1) {
          return;
        }

        const proxIds = intersectedProxs.map(
          (prox, index) =>
            `prox-${prox.track_id}-${prox.hand_id}-${prox.time_start}-${index}`
        );

        highlightCallback(proxIds, [matchingProxIndex]);
      },
      [selectedGondolaChangeGroup]
    );

    const findFirstSkuSectionChange = (skuSectionId: number) => {
      const skuSectionChangeGroups = filteredSkuSectionChangeGroups?.find(
        (group) => group.sku_section_id === skuSectionId
      );

      const skuSectionChanges =
        skuSectionChangeGroups?.sku_section_changes?.filter(
          (change) =>
            change.gondola_change_camera_composite_id ===
              selectedGondolaChange?.gondola_change_camera_composite_id &&
            change.sku_section_id === skuSectionId
        );

      return skuSectionChanges?.[0] || null;
    };

    const findFirstSkuSectionChangeWithDetection = (skuSectionId: number) => {
      const skuSectionChangeGroups =
        selectedGondolaChangeGroup?.sku_section_change_groups?.find(
          (group) => group.sku_section_id === skuSectionId
        );

      const skuSectionChanges =
        skuSectionChangeGroups?.sku_section_changes?.filter(
          (change) =>
            change.gondola_change_camera_composite_id ===
              selectedGondolaChange?.gondola_change_camera_composite_id &&
            change.sku_section_id === skuSectionId
        );

      return (
        skuSectionChanges?.find(
          (change) => change.detection || change.triangulation
        ) || null
      );
    };

    const findSkuSectionChangeWithTriangulationId = (
      skuSectionId: number,
      gondolaChangeCameraCompositeId: string,
      triangulationId: string
    ) => {
      const skuSectionChangeGroups =
        selectedGondolaChangeGroup?.sku_section_change_groups?.find(
          (group) => group.sku_section_id === skuSectionId
        );

      const skuSectionChanges =
        skuSectionChangeGroups?.sku_section_changes?.filter(
          (change) =>
            change.gondola_change_camera_composite_id ===
              gondolaChangeCameraCompositeId &&
            change.sku_section_id === skuSectionId
        );

      return (
        skuSectionChanges?.find(
          (change) => change.triangulation?.triangulation_id === triangulationId
        ) || null
      );
    };

    const getFirstFilteredGondolaChange = useCallback(
      (gondolaChangeGroupId: string | null) => {
        if (!gondolaChangeGroupId || !data) return null;

        const targetGroup = data.gondola_change_groups.find(
          (group) =>
            group.gondola_change_group_composite_id === gondolaChangeGroupId
        );

        if (!targetGroup) return null;

        const targetGroupFilteredSkuSectionChangeGroups =
          selectFilteredSkuSectionChangeGroups.resultFunc(targetGroup, filters);

        const targetGroupFilteredGondolaChanges =
          selectFilteredGondolaChanges.resultFunc(
            targetGroup,
            targetGroupFilteredSkuSectionChangeGroups
          );

        return targetGroupFilteredGondolaChanges[0] || null;
      },
      [data, filters]
    );

    return {
      data,
      filteredData,
      selectedGondolaChangeGroup,
      filteredSkuSectionChangeGroups,
      selectedSkuSectionGroup,
      filteredGondolaChanges,
      selectedGondolaChange,
      selectedSkuSectionChanges,
      selectedSkuSectionChange,
      selectedSkuSectionChangeWithDetection,
      skuSectionChangesForCurrentCamera,
      skuSectionChangesForCurrentCameraGroupTableData,
      skuSectionChangeGroupsForSelectedGondolaChange,
      loadFromFile,
      loadFromEndpoint,
      clearData,
      orderId: data?.order_id || null,
      handleSkuSectionProxsHighlighting,
      highlightSingleProx,
      findFirstSkuSectionChange,
      findFirstSkuSectionChangeWithDetection,
      findSkuSectionChangeWithTriangulationId,
      getFirstFilteredGondolaChange,
      trackAvatarBindings,
      journeyTrackRelations,
      trackIdsInOcclusion,
      recoveredTrackIdMapping,
      findTrackAvatar,
    };
  };
